import ko from 'knockout';
import _ from 'underscore';
import PageContext from '@bingads-webui-static-legacy/page-context';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import dateUtils from '@bingads-webui-component-legacy/humanize/date';
import appConfig from '@bingads-webui-component-legacy/config/index';
import kendo from '@bingads-webui/kendo/kendo.core';
import { CoOpCampaignSubType } from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/showCpgAdGroupType';
import types from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import decimalNumber from '@bingads-webui-component-legacy/humanize/decimal';
import { CampaignSubTypes, CampaignSubTypeMapping } from '@bingads-webui-campaign/audience-campaign-shared';
import { isBidLessThanFiveCentsForShoppingCampaign } from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/bidLessThanFiveCents';
import { CampaignTypeToStringMap } from '@bingads-webui-campaign/campaign-ui-common';
import { countNumberOfDigits } from '@bingads-webui-campaign-legacy/budget/shared/countNumberOfDigits';
import { GetJsDataInfo } from '@bingads-webui-clientcenter/js-data-cc-rest-base';
import { FAC_FEATURE_ID } from '@bingads-webui-campaign/recommendation-core';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import {
  isXandrServing,
  isXandrDisplayAdsCampaign,
  isXandrVideoAdsCampaign,
} from '@bingads-webui-campaign-react/constants';

const BROAD_ONLY_BID_STRATEGY_OPTIONS = new Set([
  types.targetCpa,
  types.targetRoas,
  types.maximizeConversions,
]);

const FAC_STATUS = Object.freeze({
  NOT_IN_PROGRESS: CCUIConstants.RecommendationAdoptionStatus.NotInProgess,
  ACTIVE: CCUIConstants.RecommendationAdoptionStatus.Active,
  ADOPTED: CCUIConstants.RecommendationAdoptionStatus.Adopted,
});

const AUTO_BIDDING_FEATURE_IDS = Object.freeze({
  MAX_CLICKS: parseInt(FAC_FEATURE_ID.AUTO_BIDDING_MAX_CLICKS, 10),
  MAX_CONVERSIONS: parseInt(FAC_FEATURE_ID.AUTO_BIDDING_MAX_CONVERSIONS, 10),
});

const autoBidUpfrontSpendValue = 50;

const i18n = window.globalLegacyI18n && window.globalLegacyI18n.get('module/extension/requirejs/i18n!global');

/* global _TL_ */

const percentageThreshold = 20;
const exampleBid = 12.3;
const multiplier = 100.0;

export const NumberRange = {
  costPerSale: {
    minValue: PageContext && PageContext.KeywordBidRange && PageContext.KeywordBidRange.FloorValue ? PageContext.KeywordBidRange.FloorValue : 0.01,
    maxValue: (PageContext && PageContext.KeywordBidRange && PageContext.KeywordBidRange.CeilingValue ? PageContext.KeywordBidRange.CeilingValue : 1000) * 4,
  },
  manualCpi: {
    minValue: 1,
    maxValue: 100000,
  },
  manualCpc: {
    minValue: 1,
    maxValue: 100000,
  },
  targetRoas: {
    minValue: 1,
    maxValue: 100000,
  },
  targetCpa: {
    minValue: PageContext && PageContext.KeywordBidRange && PageContext.KeywordBidRange.FloorValue ? PageContext.KeywordBidRange.FloorValue : 0.01,
    maxValue: (PageContext && PageContext.KeywordBidRange && PageContext.KeywordBidRange.CeilingValue ? PageContext.KeywordBidRange.CeilingValue : 1000) * 4,
  },
  targetImpressionShare: {
    minValue: 0.01,
    maxValue: 100,
  },
  targetCommissionRate: {
    minValue: appConfig.get('KeywordCommissionRateRange') && appConfig.get('KeywordCommissionRateRange').FloorValue,
    maxValue: appConfig.get('KeywordCommissionRateRange') && appConfig.get('KeywordCommissionRateRange').CeilingValue,
  },
  maxCpcKeyword: {
    minValue: PageContext && PageContext.KeywordBidRange && PageContext.KeywordBidRange.FloorValue ? PageContext.KeywordBidRange.FloorValue : 0.01,
    maxValue: PageContext && PageContext.KeywordBidRange && PageContext.KeywordBidRange.CeilingValue ? PageContext.KeywordBidRange.CeilingValue : 1000,
  },
  maxCpcProductGroup: {
    minValue: PageContext && PageContext.ProductGroupBidRange && PageContext.ProductGroupBidRange.FloorValue ? PageContext.ProductGroupBidRange.FloorValue : 0.01,
    maxValue: PageContext && PageContext.ProductGroupBidRange && PageContext.ProductGroupBidRange.CeilingValue ? PageContext.ProductGroupBidRange.CeilingValue : 1000,
  },
};
const outOfRangeTemplate = {
  costPerSale: 'BidStrategy_BidOutOfRangeError', // CPSTODO: out of range message
  targetRoas: 'BidStrategy_TargetROASOutOfRangeError',
  targetCpa: 'BidStrategy_BidOutOfRangeError',
  targetImpressionShare: 'BidStrategy_TargetImpressionShareOutOfRangeError',
  targetCommissionRate: 'BidStrategy_TargetCommissionRateOutOfRangeError',
  maxCpcKeyword: 'BidStrategy_BidOutOfRangeError',
  maxCpcProductGroup: 'BidStrategy_BidOutOfRangeError',
};
const outOfRange = (type) => i18n.getString(outOfRangeTemplate[type])
  .replace('{0}', decimalNumber.fromFloatToString(NumberRange[type].minValue))
  .replace('{1}', decimalNumber.fromFloatToString(NumberRange[type].maxValue));
const INVALID = i18n && i18n.getString('BidStrategy_InvalidBidError')
  .replace('{0}', decimalNumber.fromFloatToString(exampleBid, 2));

const validateNumber = function (v, type, excludeMin = false, excludeMax = false, isDirty = true) {
  if (!isDirty) {
    return '';
  }

  if (!decimalNumber.isValid(v)) {
    return INVALID;
  }

  const value = decimalNumber.fromLocalToFloat(v);
  const lowerThanMin = excludeMin ? value <= NumberRange[type].minValue : value < NumberRange[type].minValue;
  const higherThanMax = excludeMax ? value >= NumberRange[type].maxValue : value > NumberRange[type].maxValue;

  if (lowerThanMin || higherThanMax) {
    return outOfRange(type);
  }

  if (type === 'targetCommissionRate' && countNumberOfDigits(value) > 2) {
    return i18n.getString('Validation_Number_Max_Length');
  }

  return '';
};

const {
  IsCampaignTargetROASBidStrategyEnabled,
  IsNewCampaignTargetROASBidStrategyEnabled,
  IsSmartShoppingCampaignEnabled,
  IsMaximizeConversionValueEnabled,
  IsTargetImpressionShareEnabled,
  IsManualBiddingDeprecationEnabled,
  IsManualCpmEnabled,
  IsVideoAdsForCampaignEnabled,
  IsCostPerSaleEnabled,
  IsHideCpmForFeedbasedCampaginEnabled,
  IsManualCpcForVideoAdsEnabled,
  IsAudienceCampaignAutoBiddingV2Enabled,
  IsMSANMaxConversionsEnabled,
  IsEnabledDeprecateManualCpcForAudienceCampaign,
  IsDomesticSimplifiedChineseEnabled,
} = _.result(PageContext, 'Permissions', {});

const IsPortfolioBidStrategyEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'PortfolioBidStrategy', false);
const IsHotelCampaignEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'HotelCampaign', false);
const IsSponsoredPromotionsByBrandsEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'SponsoredPromotionsByBrands', false);
const IsPersonalizedOffersV4Enabled = _.result(PageContext, ['Permissions', 'IsPersonalizedOffersV4Enabled'], false);
const IsWindowsStoreAdsManualCPAEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'WindowsStoreAdsManualCPA', false);
const isMaxConversionForSscEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'SupportMaxConversionsForSsc', false);
const isPredictiveMatchingPilotEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'PredictiveMatching']);
const isRSABidStrategyAutoTargetingEnabled = !isPredictiveMatchingPilotEnabled && _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'RSABidStrategyAutoTargeting', false);
const isXandrVideoCampaignEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'XandrVideoCampaigns', false);
const isMaxConversionValueInSearchShoppingEnabled = _.result(PageContext && PageContext.Permissions, 'IsMaxConversionValueInSearchShoppingEnabled', false);
const isMaxConversionValueInSearchEnabled = _.result(PageContext && PageContext.Permissions && PageContext.Permissions.dynamic, 'MaxConversionValueInSearchCampaign', false);
const isPmaxLiteEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'PMaxLite']);
const isBroadOnlyPilotEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'BroadOnly']);
const isEnableMSANDisplayCampaignsEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'EnableMSANDisplayCampaigns']);
const isEnableMSANVideoCampaignsEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'EnableMSANVideoCampaigns']);

export default function ViewModel(model) {
  let isMaximizeConversionValueEnabled = IsMaximizeConversionValueEnabled;
  let isTargetImpressionShareEnabled = IsTargetImpressionShareEnabled;
  let isPortfolioBidStrategyEnabled = IsPortfolioBidStrategyEnabled;
  let isHotelCampaignEnabled = IsHotelCampaignEnabled;
  let isCostPerSaleEnabled = IsCostPerSaleEnabled;
  const globalFilterCampaignType = model.getCampaignTypeGlobalFilter && CampaignTypeToStringMap[model.getCampaignTypeGlobalFilter()];

  this.ccJsData = model.ccJsData;
  this.campaignSettings = model.campaignSettings;
  this.campaignType = model.campaignType; // can be undefined if isBulkEdit
  this.campaignSubType = model.campaignSubType;
  this.isCreation = model.isCreation;
  this.isBulkEdit = model.isBulkEdit;
  this.originalBidStrategy = model.biddingStrategyType;
  this.i18nNewDesign = model.i18nNewDesign;
  this.isGlobalFilterAllCampaign = this.isBulkEdit && globalFilterCampaignType === 'All';
  this.isGlobalFilterAudienceCampaign = this.isBulkEdit && globalFilterCampaignType === 'Audience';
  this.isGlobalFilterHotelCampaign = this.isBulkEdit && globalFilterCampaignType === 'Hotel';
  this.campaignBiddingStrategyType = model.campaignBiddingStrategyType;
  this.validateWithBudget = model.validateWithBudget;
  this.budgetAmount = model.budgetAmount;

  this.multiplier = multiplier;
  this.showHeader = !model.hideHeader;
  this.showBidStrategySelection = !model.isPortfolio;
  this.forceHideBidStrategySection = ko.observable(!!model.forceHideBidStrategySection);

  this.isVideo = this.campaignSubType === 'AudienceVideoAds';
  this.isFeedBased = (this.campaignSubType === 'AudienceFeedBased') || (this.campaignSubType === 'AudienceDynamicFeedBased');

  const isSponsoredCampaign = this.campaignType === 'shopping' && this.campaignSubType === 'ShoppingSponsoredProductAd';

  const isMicrosoftStoreAd = model.isMSStoreAdsGAWorkflow;

  const isSmartShopping = this.campaignType === 'shopping' && this.campaignSubType === 'ShoppingSmartAds';
  const isSmartShoppingWithMaxConversionsForSsc = isSmartShopping && isMaxConversionForSscEnabled;
  const isSearchShopping = this.campaignType === 'shopping' && !this.campaignSubType;
  const isSearchShoppingWithMaxConversionValue = isSearchShopping && isMaxConversionValueInSearchShoppingEnabled;

  const isChinaMarketAudienceCampaign = this.campaignType === 'audience' && IsDomesticSimplifiedChineseEnabled;

  this.isPmax = this.campaignType === 'performanceMax';
  this.isAppAds = this.campaignType === 'appads';
  const isBulkEditWithMaxConversionsForSsc = this.isBulkEdit && isMaxConversionForSscEnabled;

  const isCampaignTargetROASBidStrategyEnabled = IsCampaignTargetROASBidStrategyEnabled || IsNewCampaignTargetROASBidStrategyEnabled || (IsSmartShoppingCampaignEnabled && model.campaignSubType === 'ShoppingSmartAds') || this.isPmax;

  const isEnabledAutoBiddingForAudienceCampaign = this.campaignType === 'audience' &&
    [
      CampaignSubTypes.AudienceStatic,
      CampaignSubTypes.AudienceFeedBased,
      CampaignSubTypes.AudienceDynamicFeedBased,
      CampaignSubTypes.BrandAwarenessVideoAds,
      CampaignSubTypes.DisplayAds,
    ].includes(this.campaignSubType);

  this.isAudienceCampaignMaxConversionsEnabled =
    (isEnabledAutoBiddingForAudienceCampaign && (IsAudienceCampaignAutoBiddingV2Enabled || IsMSANMaxConversionsEnabled)) ||
    (isEnableMSANDisplayCampaignsEnabled && isXandrDisplayAdsCampaign(this.campaignSubType)) ||
    (isEnableMSANVideoCampaignsEnabled && isXandrVideoAdsCampaign(this.campaignSubType));

  this.isSearchCampaignMaxConversionValueEnabled = this.campaignType === 'search' && isMaxConversionValueInSearchEnabled;

  this.shouldEnableManualBiddingDeprecation = () => {
    // Only need to calculate once per bidding-strategy ViewModel
    if (this.isManualBiddingDeprecationEnabledForCurrentPath === undefined) {
      // isCampaignTypeSupportedForManualBiddingDeprecation is used for removing the manual bidding option in campaign creation and campaign settings,
      // and also for removing the bid strategy section from adgroup creation, and adgroup settings. All of which is only for the supported campaigns in this variable.
      // Note that during CPG campaign creation, bidding-strategy/view/setting.es will use updateBidStrategy to update the campaignBiddingStrategyOptions
      const isCampaignTypeSupportedForManualBiddingDeprecation = this.campaignType === 'search' || this.campaignType === 'shopping';

      // isBulkEditViaCampaignsGrid is used to disable manual bidding in the bid strategy dropdown in the top of campaigns grid form (Old and New UI).
      // The bid strategy dropdown in the toolbar for Old UI has a different source for its dropdown items, projection-grid-bid-strategy/src/menu-items.js.
      const isBulkEditViaCampaignsGrid = this.isBulkEdit && model.entityType === 'Campaign';

      const isCPG = this.campaignType === 'shopping' && (this.campaignSubType === 'ShoppingCoOperative' || this.campaignSubType === CoOpCampaignSubType);

      // For existing campaigns of type Search/DSA/non-CPG Shopping that have manual CPC, disable MBD in Campaign Settings.
      // The !isCPG check is covered in this.isManualBiddingDeprecationEnabledForCurrentPath.
      const isCampaignSettingsWhereCampaignHasManualCPC = model.entityType === 'Campaign' &&
        !this.isCreation && !this.isBulkEdit &&
        isCampaignTypeSupportedForManualBiddingDeprecation &&
        this.originalBidStrategy === types.manual;

      this.isManualBiddingDeprecationEnabledForCurrentPath = IsManualBiddingDeprecationEnabled &&
        (isCampaignTypeSupportedForManualBiddingDeprecation || isBulkEditViaCampaignsGrid) &&
        !isCPG &&
        !isMicrosoftStoreAd &&
        !isCampaignSettingsWhereCampaignHasManualCPC &&
        // In campaigns grid, ManualCpc is hidden only when user selects non-audience campaign waterfall filter in the left nav like "Search campaigns".
        !this.isGlobalFilterAllCampaign &&
        !this.isGlobalFilterAudienceCampaign &&
        !this.isGlobalFilterHotelCampaign;

      if (isEnabledAutoBiddingForAudienceCampaign && IsEnabledDeprecateManualCpcForAudienceCampaign) {
        this.isManualBiddingDeprecationEnabledForCurrentPath = true;
      }
    }

    return this.isManualBiddingDeprecationEnabledForCurrentPath;
  };

  this.determineSupportedCampaignBiddingStrategyOptions = () => {
    this.campaignBiddingStrategyOptions = [];

    if (isSmartShoppingWithMaxConversionsForSsc) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.maximizeConversions,
        Text: i18n.getString('BidStrategy_Option_MaximizeConversions'),
      });
      this.campaignBiddingStrategyOptions.push({
        Value: types.maxConversionValue,
        Text: i18n.getString('BidStrategy_Option_MaxConversionValue'),
      });

      // No need for any other strategyt for SSC creation flow with MaxConversion support
      return;
    }

    // auto bidding show come before manual ones.
    if (this.campaignType === 'audience' &&
      [
        CampaignSubTypes.AudienceStatic,
        CampaignSubTypes.AudienceFeedBased,
        CampaignSubTypes.AudienceDynamicFeedBased,
        CampaignSubTypes.BrandAwarenessVideoAds,
        CampaignSubTypes.DisplayAds,
        CampaignSubTypeMapping.BrandAwarenessVideoAds,
        CampaignSubTypeMapping.DisplayAds,
      ].includes(this.campaignSubType)) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.enhancedCPC,
        Text: i18n.getString('BidStrategy_Option_EnhancedCPC'),
      });
    }

    if (this.isAudienceCampaignMaxConversionsEnabled) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.maximizeConversions,
        Text: i18n.getString('BidStrategy_Option_MaximizeConversions'),
      });
    }

    if (this.isPmax) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.maximizeConversions,
        Text: i18n.getString('BidStrategy_Option_MaximizeConversions'),
      });
      this.campaignBiddingStrategyOptions.push({
        Value: types.maxConversionValue,
        Text: i18n.getString('BidStrategy_Option_MaxConversionValue'),
      });

      // No need for any other strategyt for pmax creation flow with MaxConversion support
      return;
    }

    if (this.isAppAds) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.enhancedCPC,
        Text: i18n.getString('BidStrategy_Option_Manual_CPI'),
      });
      return;
    }

    if (!this.shouldEnableManualBiddingDeprecation()) {
      if ((this.isBulkEdit || !this.isVideo || IsManualCpcForVideoAdsEnabled || isMicrosoftStoreAd) &&
        (!(isEnableMSANDisplayCampaignsEnabled && isXandrDisplayAdsCampaign(this.campaignSubType)) &&
          !(isEnableMSANVideoCampaignsEnabled && isXandrVideoAdsCampaign(this.campaignSubType)))) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.manual,
          Text: i18n.getString('BidStrategy_Option_Manual_Campaign'),
        });
      }
    }
    /**
   * Manual CPV is enabled in 2 cases:
   *   1. Campaigns grid bulk edit and under video pilot
   *   2. Setting page of video campaign
   */
    if (
      (this.isBulkEdit && model.entityType === 'Campaign' && IsVideoAdsForCampaignEnabled) ||
      (!this.isBulkEdit && this.isVideo)) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.manualCpv,
        Text: isXandrVideoCampaignEnabled ? i18n.getString('BidStrategy_Option_Manual_CPV_CPCV_Campaign') : i18n.getString('BidStrategy_Option_Manual_CPV_Campaign'),
      });
    }
    /**
   * Manual CPM is enabled in 2 cases when user is under CPM pilot:
   *   1. Campaigns grid bulk edit
   *   2. Audience campaign setting page
   */
    if (IsManualCpmEnabled) {
      if (this.isBulkEdit || this.campaignType === 'audience') {
        this.campaignBiddingStrategyOptions.push({
          Value: types.manualCpm,
          Text: isXandrVideoCampaignEnabled ? i18n.getString('BidStrategy_Option_Manual_Or_Viewable_CPM_Campaign') : i18n.getString('BidStrategy_Option_Manual_CPM_Campaign'),
        });
      }
    }

    if (IsHideCpmForFeedbasedCampaginEnabled) {
      if (!this.isBulkEdit && this.isFeedBased) {
        this.campaignBiddingStrategyOptions = this.campaignBiddingStrategyOptions.filter((option) => option.Value !== types.manualCpm);
      }
    }

    if (IsWindowsStoreAdsManualCPAEnabled && (isMicrosoftStoreAd || this.isBulkEdit)) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.manualCpa,
        Text: i18n.getString('BidStrategy_Option_Manual_CPA_Campaign'),
      });
    }

    if (this.campaignType !== 'audience' && !isMicrosoftStoreAd) {
      this.campaignBiddingStrategyOptions.push({
        Value: types.enhancedCPC,
        Text: i18n.getString('BidStrategy_Option_EnhancedCPC'),
      });

      this.campaignBiddingStrategyOptions.push({
        Value: types.maximizeClicks,
        Text: i18n.getString('BidStrategy_Option_MaximizeClicks'),
      });

      if (this.campaignType !== 'shopping' || isBulkEditWithMaxConversionsForSsc) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.maximizeConversions,
          Text: i18n.getString('BidStrategy_Option_MaximizeConversions'),
        });
      }

      if (isMaxConversionValueInSearchEnabled && this.campaignType === 'search') {
        this.campaignBiddingStrategyOptions.push({
          Value: types.maxConversionValue,
          Text: i18n.getString('BidStrategy_Option_MaxConversionValue'),
        });
      }

      if (this.campaignType !== 'shopping') {
        this.campaignBiddingStrategyOptions.push({
          Value: types.targetCpa,
          Text: i18n.getString('BidStrategy_Option_TargetCPA'),
        });
      }

      if (isCampaignTargetROASBidStrategyEnabled) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.targetRoas,
          Text: i18n.getString('BidStrategy_Option_TargetROAS'),
        });
      }

      const showMaxConversionValue =
        (isMaximizeConversionValueEnabled && (this.campaignType === 'search' || this.campaignType === 'shopping' || this.isPmax || this.isBulkEdit)) ||
        isBulkEditWithMaxConversionsForSsc || isSearchShoppingWithMaxConversionValue;

      if (showMaxConversionValue) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.maxConversionValue,
          Text: i18n.getString('BidStrategy_Option_MaxConversionValue'),
        });
      }

      if (isTargetImpressionShareEnabled && (this.campaignType === 'search' || this.isBulkEdit)) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.targetImpressionShare,
          Text: i18n.getString('BidStrategy_Option_TargetImpressionShare'),
        });
      }

      if (isPortfolioBidStrategyEnabled &&
        (this.campaignType === 'search' || this.campaignType === 'shopping' || this.isBulkEdit) &&
        // isPortfolio means it is creating a portoflio from portfolio bid strategies grid. Need to exclude this case.
        !this.isPortfolio) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.portfolioBidStrategy,
          Text: i18n.getString('BidStrategy_Option_PortfolioBidStrategy'),
        });
      }

      if (isHotelCampaignEnabled && (this.campaignType === 'hotel' || this.isBulkEdit)) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.percentCpc,
          Text: i18n.getString('BidStrategy_Option_PercentCpc'),
        });
        this.campaignBiddingStrategyOptions.push({
          Value: types.commission,
          Text: i18n.getString('BidStrategy_Option_Commission'),
        });
      }

      if (IsCostPerSaleEnabled && IsPersonalizedOffersV4Enabled &&
        this.campaignType === 'shopping' && this.campaignSubType == null && (this.isCreation || this.originalBidStrategy === types.costPerSale)) {
        const costPerSaleBidStrategy = {
          Value: 'CostPerSale',
          Text: i18n.getString('BidStrategy_Option_CostPerSale'),
        };

        this.campaignBiddingStrategyOptions.push(costPerSaleBidStrategy);
      }
    }

    if (this.campaignType === 'audience' && isXandrServing(this.campaignSubType, this.campaignSettings) &&
      (!(isEnableMSANDisplayCampaignsEnabled && isXandrDisplayAdsCampaign(this.campaignSubType)) &&
        !(isEnableMSANVideoCampaignsEnabled && isXandrVideoAdsCampaign(this.campaignSubType)))) {
      this.campaignBiddingStrategyOptions = [{
        Value: types.manualCpm,
        Text: i18n.getString('BidStrategy_Option_Viewable_CPM_Campaign'),
      }];
    }

    if (IsDomesticSimplifiedChineseEnabled && !isMicrosoftStoreAd) {
      this.campaignBiddingStrategyOptions = [{
        Value: types.enhancedCPC,
        Text: i18n.getString('BidStrategy_Option_EnhancedCPC'),
      }];

      if (this.campaignType === 'search' || this.isBulkEdit) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.maximizeClicks,
          Text: i18n.getString('BidStrategy_Option_MaximizeClicks'),
        });

        this.campaignBiddingStrategyOptions.push({
          Value: types.maximizeConversions,
          Text: i18n.getString('BidStrategy_Option_MaximizeConversions'),
        });

        this.campaignBiddingStrategyOptions.push({
          Value: types.targetCpa,
          Text: i18n.getString('BidStrategy_Option_TargetCPA'),
        });

        this.campaignBiddingStrategyOptions.push({
          Value: types.targetRoas,
          Text: i18n.getString('BidStrategy_Option_TargetROAS'),
        });

        this.campaignBiddingStrategyOptions.push({
          Value: types.portfolioBidStrategy,
          Text: i18n.getString('BidStrategy_Option_PortfolioBidStrategy'),
        });
      }

      if (this.campaignType === 'audience' || this.isBulkEdit) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.maximizeConversions,
          Text: i18n.getString('BidStrategy_Option_MaximizeConversions'),
        });
      }

      if (this.campaignType === 'shopping' || this.isBulkEdit) {
        this.campaignBiddingStrategyOptions.push({
          Value: types.maximizeClicks,
          Text: i18n.getString('BidStrategy_Option_MaximizeClicks'),
        });
      }
    }

    // Make sure we dont have any duplicates
    this.campaignBiddingStrategyOptions = [...new Set(this.campaignBiddingStrategyOptions)];
  };

  this.determineSupportedCampaignBiddingStrategyOptions();

  const adGroupBiddingStrategyOptions = [{
    Value: types.parent,
    Text: i18n.getString('BidStrategy_Option_ParentCampaign'),
  }, {
    Value: types.manual,
    Text: i18n.getString('BidStrategy_Option_Manual'),
  }];

  if (this.isVideo) {
    adGroupBiddingStrategyOptions.pop();
  }

  const keywordBiddingStrategyOptions = [{
    Value: types.parent,
    Text: i18n.getString('BidStrategy_Option_ParentAdGroup'),
  }, {
    Value: types.manual,
    Text: i18n.getString('BidStrategy_Option_Manual'),
  }];

  this.strategyOptions = ko.observable({
    'Campaign': this.campaignBiddingStrategyOptions,
    'AdGroup': adGroupBiddingStrategyOptions,
    'Keyword': keywordBiddingStrategyOptions,
  }[model.entityType] || []);

  this.targetImpressionSharePositionOptions = [
    {
      Value: 'FirstPage',
      Text: i18n.getString('BidStrategySetting_TargetImpressionShare_AdPosition_Anywhere'),
    },
    {
      Value: 'MainLine',
      Text: i18n.getString('BidStrategySetting_TargetImpressionShare_AdPosition_Top'),
    },
    {
      Value: 'MainLine1',
      Text: i18n.getString('BidStrategySetting_TargetImpressionShare_AdPosition_AbsoluteTop'),
    },
  ];

  this.selectedBidStrategy = ko.observable(model.biddingStrategyType);
  this.selectedPortfolioBidStrategy = ko.observable(this.originalBidStrategy === types.portfolioBidStrategy ? { Name: model.bidStrategyName, Type: model.portfolioBidStrategyType, Id: model.biddingStrategyId } : null);
  this.isAfterSave = ko.observable(model.isAfterSave);
  this.forceReadOnly = ko.observable(false);
  this.isReadOnly = ko.computed(() =>
    (IsSponsoredPromotionsByBrandsEnabled && this.forceReadOnly()) ||
    (this.isAfterSave() && this.selectedBidStrategy() === types.costPerSale) ||
    (isXandrServing(this.campaignSubType, this.campaignSettings) && (!(isEnableMSANDisplayCampaignsEnabled && isXandrDisplayAdsCampaign(this.campaignSubType)) &&
      !(isEnableMSANVideoCampaignsEnabled && isXandrVideoAdsCampaign(this.campaignSubType)))) ||
    (this.isAfterSave() && isMicrosoftStoreAd && IsWindowsStoreAdsManualCPAEnabled),
  );

  this.showTargetCpaCheckbox = ko.computed(() => {
    return this.selectedBidStrategy() === types.maximizeConversions &&
      (this.isAudienceCampaignMaxConversionsEnabled || isSmartShoppingWithMaxConversionsForSsc || this.isPmax || isBulkEditWithMaxConversionsForSsc || isChinaMarketAudienceCampaign);
  });

  // The actual bid strategy type applied to campaign
  // 1. if the selected strategy is a individual bid strategy, then it's the individual bid strategy itself
  // 2. if the selected strategy is a portfolio bid strategy, then it's the bid strategy type of the portfolio
  this.actualBidStrategy = ko.computed(() => {
    if (this.selectedBidStrategy() === types.portfolioBidStrategy && this.selectedPortfolioBidStrategy()) {
      return this.selectedPortfolioBidStrategy().Type;
    }

    return this.selectedBidStrategy();
  });

  this.isInitialize = model.isInitialize;
  this.isConversionChecking = ko.observable(true);

  this.errorMessageMaxCpc = ko.observable();
  this.errorMessageTargetCpa = ko.observable();
  this.errorMessageTargetRoas = ko.observable();
  this.errorMessageTargetImpressionSharePercent = ko.observable();
  this.errorMessageMaxConversionValue = ko.observable();
  this.errorMessagePortfolioBidStrategy = ko.observable();
  this.errorMessageCostPerSale = ko.observable();
  this.errorMessageTargetCommissionRate = ko.observable();
  this.maxCpcDefaultValueAlertMessge = ko.observable();
  this.targetCpaDefaultValueAlertMessge = ko.observable();
  this.targetRoasDefaultValueAlertMessge = ko.observable();
  this.targetCommissionRateDefaultValueAlertMessge = ko.observable();
  this.costPerSaleDefaultValueAlertMessge = ko.observable();

  // show a message for campaign level's setting page and bulk edit UI
  // when the type change to other than manual.
  this.showAutobiddingChangeAlert = ko.computed(() => model.entityType === 'Campaign' &&
    !this.isCreation && // setting UI or bulk edit UI
    !this.isPmax && //not showing on Pmax
    !_.contains([types.manual, types.manualCpv, types.manualCpm, types.commission, types.percentCpc], this.selectedBidStrategy()) &&
    (this.isBulkEdit || this.selectedBidStrategy() !== this.originalBidStrategy));

  this.selectedBidStrategy.subscribe((value) => {
    this.errorMessageMaxCpc('');
    this.errorMessageTargetCpa('');
    this.errorMessageTargetRoas('');
    this.errorMessageMaxConversionValue('');
    this.errorMessageCostPerSale('');
    this.errorMessageTargetCommissionRate('');
    if (value === types.maximizeConversions && this.campaignType === 'search') {
      this.isTargetCpaCheckboxSelected(false);
    }
    if (model.activityLogger) {
      model.activityLogger.signal(`bidding strategy type change: ${value}`, 'bidding strategy');
    }
  }, this);

  this.isPersonalizedOffersEnabled = ko.observable(model.shouldSupportCashbackSettings);
  this.isTargetRoasCheckboxSelected = ko.observable(model.targetRoas != null);
  this.isBidStrategyReadonly = ko.computed(() => {
    return isSmartShopping && !isMaxConversionForSscEnabled;
  });

  // TODO: remove else path after GA of manual bidding deprecation
  if (IsManualBiddingDeprecationEnabled) {
    // The code in adgroup-create-advanced/view/steps/settings.es will now pass in campaignType and campaignSubType,
    // we don't want to change selected bid strategy for ad groups so enforce the campaign entity type when in manual bidding deprecation pilot.
    if (model.entityType === 'Campaign' && isSmartShopping && !isMaxConversionForSscEnabled) {
      this.selectedBidStrategy(types.maxConversionValue);
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if ((this.campaignType === 'shopping' && this.campaignSubType === 'ShoppingSmartAds') || this.isPmax) {
      this.selectedBidStrategy(types.maxConversionValue);
    }
  }

  this.showTargetRoasCheckbox = ko.computed(() => {
    return this.selectedBidStrategy() === types.maxConversionValue &&
      (this.isSearchCampaignMaxConversionValueEnabled || isSmartShopping || this.isPmax || isBulkEditWithMaxConversionsForSsc || isSearchShoppingWithMaxConversionValue);
  });

  this.showCostPerSale = ko.computed(() => {
    return this.selectedBidStrategy() === types.costPerSale &&
      !this.isBulkEdit;
  });

  this.showConversionChecking = ko.computed(() => {
    return (this.selectedBidStrategy() === types.enhancedCPC) &&
      !this.isConversionChecking();
  });

  this.isTargetCpaCheckboxSelected = ko.observable(model.targetCpa != null);

  this.showTargetCPA = ko.computed(() => {
    const targetCpaInputNeeded = this.selectedBidStrategy() === types.targetCpa || (this.selectedBidStrategy() === types.maximizeConversions && this.isTargetCpaCheckboxSelected());

    return targetCpaInputNeeded && !this.showConversionChecking();
  });

  this.showCPC = ko.computed(() => {
    return (this.selectedBidStrategy() === types.maximizeClicks ||
      (this.selectedBidStrategy() === types.maximizeConversions && !isSmartShoppingWithMaxConversionsForSsc && !this.isPmax) ||
      this.selectedBidStrategy() === types.targetCpa ||
      this.selectedBidStrategy() === types.targetRoas ||
      this.selectedBidStrategy() === types.targetImpressionShare) &&
      !this.showConversionChecking();
  });

  this.showAutoTargeting = ko.computed(() => {
    const supportedBidStrategies = [
      types.targetCpa,
      types.targetRoas,
      types.maximizeConversions,
    ];

    return this.showBidStrategySelection && (model.campaignType === 'search' || model.isBulkEdit) && isRSABidStrategyAutoTargetingEnabled && supportedBidStrategies.indexOf(this.selectedBidStrategy()) > -1;
  });

  this.showPredictiveMatching = ko.computed(() => {
    const supportedBidStrategies = [
      types.targetCpa,
      types.targetRoas,
      types.maximizeConversions,
    ];

    return this.showBidStrategySelection && model.isBulkEdit && isPredictiveMatchingPilotEnabled && supportedBidStrategies.indexOf(this.selectedBidStrategy()) > -1;
  });

  this.showTargetROAS = ko.computed(() => {
    return (this.selectedBidStrategy() === types.targetRoas ||
      (this.selectedBidStrategy() === types.maxConversionValue && this.isTargetRoasCheckboxSelected() &&
        (this.isSearchCampaignMaxConversionValueEnabled || isSmartShopping || this.isPmax || isBulkEditWithMaxConversionsForSsc || isSearchShoppingWithMaxConversionValue))) &&
      !this.showConversionChecking();
  });

  this.showTargetImpressionShare = ko.computed(() => {
    return this.selectedBidStrategy() === types.targetImpressionShare;
  });

  this.showCommissionRate = ko.computed(() => {
    return this.selectedBidStrategy() === types.commission;
  });

  this.checkCPC = ko.observable(false);
  this.checkCPC.subscribe((value) => {
    if (model.activityLogger) {
      model.activityLogger.signal(`set max CPC: ${value}`, 'bidding strategy');
    }
  });

  this.checkAutoTargeting = ko.observable(model.autoTargetingEnabled);
  this.checkAutoTargeting.subscribe((value) => {
    if (model.activityLogger) {
      model.activityLogger.signal(`set auto targeting: ${value}`, 'bidding strategy');
    }
  });

  this.predictiveMatchingChecked = ko.observable(false);
  this.predictiveMatchingChecked.subscribe((value) => {
    if (model.activityLogger) {
      model.activityLogger.signal(`set predictive matching: ${value}`, 'bidding strategy');
    }
  });

  const logMaxCpcChange = _.once(() => {
    if (model.activityLogger) {
      model.activityLogger.signal('max CPC value change', 'bidding strategy');
    }
  });

  this.imaxCPCBidBase = ko.observable(-1);
  this.maxCpcBid = ko.observable();
  this.isMaxCpcDirty = ko.observable(false);
  this.maxCpc = ko.computed({
    read: () => {
      return this.maxCpcBid();
    },
    write: (value) => {
      this.errorMessageMaxCpc('');
      this.maxCpcBid(value);
      logMaxCpcChange();
      if (this.checkConversionCountAndRevenue) {
        this.checkConversionCountAndRevenue();
      }
    },
    owner: this,
  });
  this.showMaxCpcDefaultValueAlert = ko.computed(() => {
    if (this.selectedBidStrategy() === types.maximizeClicks ||
      this.selectedBidStrategy() === types.maximizeConversions ||
      this.selectedBidStrategy() === types.targetCpa ||
      (this.selectedBidStrategy() === types.targetRoas || this.isTargetRoasCheckboxSelected())) {
      if (this.checkCPC()) {
        const maxCpc = this.maxCpc();
        const maxCpcKey = `maxCpc${isBidLessThanFiveCentsForShoppingCampaign(this.campaignType, this.isBulkEdit) ? 'ProductGroup' : 'Keyword'}`;

        if (maxCpc !== undefined && this.isMaxCpcDirty() === false) {
          this.isMaxCpcDirty(true);
        }
        const result = validateNumber(maxCpc, maxCpcKey, false, false, this.isMaxCpcDirty());

        if (!_.isEmpty(result)) {
          return false;
        }
        const realValue = decimalNumber.fromLocalToFloat(maxCpc);

        if (this.imaxCPCBidBase() >= 0) {
          if (realValue < this.imaxCPCBidBase() * (1 - (percentageThreshold / 100))) {
            this.maxCpcDefaultValueAlertMessge(i18n.getString(
              'BidStrategySetting_Message_MaxCPC_LessAlert',
              maxCpc,
              decimalNumber.fromFloatToString(this.imaxCPCBidBase(), 2),
              decimalNumber.fromFloatToString(percentageThreshold, 0),
            ));
            return true;
          }
        }
      }
    }
    return false;
  });

  this.itargetCpaBidBase = ko.observable(-1);
  this.targetCpaBid = ko.observable();
  this.isTargetCpaDirty = ko.observable(false);
  this.targetCpa = ko.computed({
    read: () => {
      return this.targetCpaBid();
    },
    write: (value) => {
      this.errorMessageTargetCpa('');
      this.targetCpaBid(value);
    },
    owner: this,
  });
  this.showTargetCpaDefaultValueAlert = ko.computed(() => {
    if (this.selectedBidStrategy() !== types.targetCpa && !this.isTargetCpaCheckboxSelected()) {
      return false;
    }
    const targetCpa = this.targetCpa();

    if (targetCpa !== undefined && this.isTargetCpaDirty() === false) {
      this.isTargetCpaDirty(true);
    }
    const result = validateNumber(targetCpa, 'targetCpa', false, false, this.isTargetCpaDirty());

    if (!_.isEmpty(result)) {
      this.targetCpaDefaultValueAlertMessge(result);
      return true;
    }

    const realValue = decimalNumber.fromLocalToFloat(targetCpa);

    if (this.itargetCpaBidBase() >= 0) {
      if (realValue < this.itargetCpaBidBase() * (1 - (percentageThreshold / 100))) {
        this.targetCpaDefaultValueAlertMessge(i18n.getString(
          'BidStrategySetting_Message_TargetCPA_LessAlert',
          targetCpa,
          decimalNumber.fromFloatToString(this.itargetCpaBidBase(), 2),
          decimalNumber.fromFloatToString(percentageThreshold, 0),
        ));
        return true;
      }
    }
    return false;
  });

  // Commission Rate section
  this.targetCommissionRateValue = ko.observable();
  this.targetCommissionRate = ko.computed({
    read: () => {
      return this.targetCommissionRateValue();
    },
    write: (value) => {
      this.errorMessageTargetCommissionRate('');
      this.targetCommissionRateValue(value);
    },
    owner: this,
  });
  this.showTargetCommissionRateDefaultValueAlert = ko.computed(() => {
    if (this.selectedBidStrategy() !== types.commission) {
      return false;
    }
    const targetCommissionRate = this.targetCommissionRate();
    const result = validateNumber(targetCommissionRate, 'targetCommissionRate', true);

    if (!_.isEmpty(result)) {
      this.targetCommissionRateDefaultValueAlertMessge(result);
      return true;
    }
    return false;
  });


  // ROAS section
  this.itargetRoasBidBase = ko.observable(-1);
  this.targetRoasBid = ko.observable();
  this.targetRoas = ko.computed({
    read: () => {
      return this.targetRoasBid();
    },
    write: (value) => {
      this.errorMessageTargetRoas('');
      this.targetRoasBid(value);
    },
    owner: this,
  });
  this.showTargetRoasDefaultValueAlert = ko.computed(() => {
    if (this.selectedBidStrategy() !== types.targetRoas && !this.isTargetRoasCheckboxSelected()) {
      return false;
    }
    const targetRoas = this.targetRoas();
    const result = validateNumber(targetRoas, 'targetRoas');

    if (!_.isEmpty(result)) {
      this.targetRoasDefaultValueAlertMessge(result);
      return true;
    }
    const realValue = decimalNumber.fromLocalToFloat(targetRoas);

    if (this.itargetRoasBidBase() >= 0) {
      if (realValue < this.itargetRoasBidBase() * 0.8) {
        this.targetRoasDefaultValueAlertMessge(i18n.getString('BidStrategy_TargetROAS_LessWarning', this.itargetRoasBidBase()));
        return true;
      }
      if (realValue > this.itargetRoasBidBase() * 1.2) {
        this.targetRoasDefaultValueAlertMessge(i18n.getString('BidStrategy_TargetROAS_MoreWarning', this.itargetRoasBidBase()));
        return true;
      }
    }
    return false;
  });

  // TIS section
  this.targetImpressionShareAdPosition = ko.observable(model.targetAdPosition || 'FirstPage');
  this.targetImpressionShareAdPositionUpdate = ko.computed({
    read: () => {
      return this.targetImpressionShareAdPosition();
    },
    write: (value) => {
      this.targetImpressionShareAdPosition(value);
    },
  });
  this.targetImpressionSharePercent = ko.observable(model.targetImpressionShare);
  this.targetImpressionShare = ko.computed({
    read: () => {
      return this.targetImpressionSharePercent();
    },
    write: (value) => {
      this.errorMessageTargetImpressionSharePercent('');
      this.targetImpressionSharePercent(value);
    },
    owner: this,
  });

  // PBS section
  this.showPortfolioSection = ko.computed(() => {
    return this.selectedBidStrategy() === types.portfolioBidStrategy;
  });

  if (this.originalBidStrategy === types.portfolioBidStrategy) {
    // when the original bid strategy is portfolio bid strategy, at the beginning display current selected
    // portfolio bid strategy instead of the portfolio selector
    this.showPortfolioSelector = ko.observable(false);
    this.originalPortfolioBidStrategy = `${model.bidStrategyName} (${i18n.getString(types.biddingStrategyOption[model.portfolioBidStrategyType])}) `;
  } else {
    this.showPortfolioSelector = ko.observable(true);
    this.originalPortfolioBidStrategy = '';
  }

  // CPS section
  this.costPerSaleValue = ko.observable(model.costPerSale);
  this.costPerSale = ko.computed({
    read: () => {
      return this.costPerSaleValue();
    },
    write: (value) => {
      if (!model.isRenderedInReact) {
        this.errorMessageCostPerSale('');
      }

      this.costPerSaleValue(value);
    },
    owner: this,
  });
  this.showCostPerSaleDefaultValueAlert = ko.computed(() => {
    if (this.selectedBidStrategy() !== types.costPerSale) {
      return false;
    }
    const costPerSale = this.costPerSale();
    const result = validateNumber(costPerSale, 'costPerSale');

    if (!_.isEmpty(result)) {
      this.costPerSaleDefaultValueAlertMessge(result);
      return true;
    }
    return false;
  });

  if (model.maxCpc) {
    this.checkCPC(true);
    this.maxCpc(decimalNumber.fromFloatToString(model.maxCpc, 2));
  }

  if (model.targetCpa) {
    this.isTargetCpaCheckboxSelected(true);
    this.targetCpa(decimalNumber.fromFloatToString(model.targetCpa, 2));
  }

  if (model.targetRoas) {
    this.targetRoas(decimalNumber.fromFloatToString(model.targetRoas * this.multiplier, 2));
  }

  if (model.costPerSale) {
    this.costPerSale(decimalNumber.fromFloatToString(model.costPerSale, 2));
  }

  this.setConversionChecking = function (value) {
    if (this.isConversionChecking() !== value) {
      this.isConversionChecking(value);
      this.isConversionChecking.notifySubscribers();
      if (value === false && this.isInitialize === true && this.selectedBidStrategy() === types.enhancedCPC) {
        this.isInitialize = false;
        // this.selectedBidStrategy(types.manual);
      }
    }
  };

  this.checkConversionTracking = function () {
    const self = this;

    if (this.selectedBidStrategy() === types.enhancedCPC) {
      const today = new Date();
      const startDate = dateUtils.format(kendo.date.addDays(today, -30), 'M/d/yyyy');
      const endDate = dateUtils.format(today, 'M/d/yyyy');
      const url = `/Customers(${PageContext.CurrentAccount.AdvertiserCustomerID})/Accounts(${PageContext.CurrentAccount.Id})/Goals?startdate=${startDate}&enddate=${endDate}&$top=1&$orderby=Name&$filter=(TrackingStatus+eq+Enum.GoalTrackingStatus'NoRecentConversion'+or+TrackingStatus+eq+Enum.GoalTrackingStatus'RecordingConversions')&$count=true`;

      odata.get(url)
        .then((response) => {
          if (!response || !response.value || response.value.length < 1) {
            self.setConversionChecking(false); // no matched tracking status
          } else {
            self.setConversionChecking(true);
          }
        })
        .catch(() => {
          // ignore the error case and do not block the user because the default is no op
          self.setConversionChecking(true);
        });
    }
  };

  // auto-bidding coupon notification
  this.dismissCouponNotification = function () {
    this.isAutoBiddingCouponNotificationDismissed(true);
  };

  this.getAutoBiddingCoupon = function () {
    // use the ccJsData instance from the parent if it exists to avoid duplicate call
    let ccJsDataInstance = this.ccJsData;

    if (!ccJsDataInstance) {
      // eslint-disable-next-line new-cap
      ccJsDataInstance = GetJsDataInfo({
        cidFetcher: () => PageContext.CurrentCustomer.Id,
        uidFetcher: () => PageContext.CurrentUser.Id,
        willSupportCors: true,
      }, PageContext.CCBaseUrl, ['FeatureAdoptionCoupon']).jsDataCcRest;
    }

    ccJsDataInstance.FeatureAdoptionCoupon
      .findAll({ aid: PageContext.CurrentAccount.Id })
      .then((coupons) => _.filter(
        coupons,
        ({ Status, FeatureId }) => (FeatureId === AUTO_BIDDING_FEATURE_IDS.MAX_CLICKS || FeatureId === AUTO_BIDDING_FEATURE_IDS.MAX_CONVERSIONS) &&
          Status === FAC_STATUS.ACTIVE,
      )).then((coupons) => {
        if (coupons.length > 0) {
          this.autoBiddingCoupon(coupons[0]);
        } else {
          this.autoBiddingCoupon(null);
        }
      });
  };

  // TODO: handle internationalized currency
  this.autoBidMaxClicksDescription = i18n.getString('BidStrategySettings_Auto_Bid_Coupon_Description_Max_Clicks').replace('{0}', `$${autoBidUpfrontSpendValue}`);
  this.autoBidMaxConversionsDescription = i18n.getString('BidStrategySettings_Auto_Bid_Coupon_Description_Max_Conversions').replace('{0}', `$${autoBidUpfrontSpendValue}`);
  this.autoBiddingCoupon = ko.observable(null);
  this.autoBiddingCoupon(this.getAutoBiddingCoupon());
  this.isAutoBiddingCouponNotificationDismissed = ko.observable(false);

  this.shouldDisplayNotification = function () {
    return this.autoBiddingCoupon() && !this.isAutoBiddingCouponNotificationDismissed();
  };

  this.autoBiddingCouponDescription = ko.computed(() => {
    if (this.autoBiddingCoupon()) {
      switch (this.autoBiddingCoupon().FeatureId) {
      case AUTO_BIDDING_FEATURE_IDS.MAX_CLICKS:
        return this.autoBidMaxClicksDescription;
      case AUTO_BIDDING_FEATURE_IDS.MAX_CONVERSIONS:
        return this.autoBidMaxConversionsDescription;
      default:
        return null;
      }
    }

    return null;
  });

  // When PmaxLite pilot is enabled we need to skip the conversion check
  this.shouldSkipConversionCheck = ko.computed(() => this.isPmax || this.isBulkEdit);
  // initialize with -1 to indicated not get odata result yet. 0 means not met and 1 means campaign met the conversion goal >15
  // IsActiveConversionGoalPresent is for this.meetConversionGoal
  this.meetConversionGoal = ko.observable(this.shouldSkipConversionCheck() ? 1 : -1);
  // initialize with -1 to indicated not get odata result yet. 0 means not met and 1 means campaign met the revenue goal >0
  // IsActiveConversionGoalPresentWithValidRevenueValue is for this.meetRevenueGoal
  this.meetRevenueGoal = ko.observable(this.shouldSkipConversionCheck() ? 1 : -1);
  this.checkConversionCountAndRevenueCalled = false;

  this.checkConversionCountAndRevenue = () => {
    if (this.shouldSkipConversionCheck()) {
      return;
    }

    if (this.checkConversionCountAndRevenueCalled) {
      return;
    }
    this.checkConversionCountAndRevenueCalled = true;

    {
      const url = `/Customers(${PageContext.CurrentAccount.AdvertiserCustomerID})/Accounts(${PageContext.CurrentAccount.Id})/ConversionGoalDetails`;

      odata.get(url)
        .then((response) => {
          this.meetConversionGoal(response.IsActiveConversionGoalPresent ? 1 : 0);
          this.meetRevenueGoal(response.IsActiveConversionGoalPresentWithValidRevenueValue ? 1 : 0);
        })
        .catch(() => {
          this.meetConversionGoal(0);
          this.meetRevenueGoal(0);
        });
    }

    if (PageContext.CurrentCampaign && PageContext.CurrentCampaign.Id) {
      const utcnow = dateUtils.utcNow();

      utcnow.setDate(utcnow.getDate() - 1);

      const endDate = dateUtils.format(utcnow, 'M/d/yyyy');

      utcnow.setDate(utcnow.getDate() - 29);

      const startDate = dateUtils.format(utcnow, 'M/d/yyyy');
      const url = `/Customers(${PageContext.CurrentAccount.AdvertiserCustomerID})/Accounts(${PageContext.CurrentAccount.Id})/Campaigns?startdate=${startDate}&enddate=${endDate}&$select=Id,Name,PerformanceMetrics&$expand=Budget&$filter=Id+eq+${PageContext.CurrentCampaign.Id}&$top=1`;

      odata.get(url)
        .then((response) => {
          if (response && response.value && response.value.length > 0 && response.value[0].PerformanceMetrics) {
            this.itargetCpaBidBase(parseFloat(response.value[0].PerformanceMetrics.CPA));
            if (_.isUndefined(this.targetCpaBid())) {
              this.targetCpaBid(decimalNumber.fromFloatToString(this.itargetCpaBidBase(), 2));
            }
            this.imaxCPCBidBase(parseFloat(response.value[0].PerformanceMetrics.AverageCPC));
            this.itargetRoasBidBase(parseFloat(response.value[0].PerformanceMetrics.RevenueOnAdSpend));
            if (_.isUndefined(this.targetRoasBid())) {
              this.targetRoasBid(decimalNumber.fromFloatToString(this.itargetRoasBidBase(), 2));
            }
          }
        })
        .catch(_.noop);
    }
  };

  this.checkErrorMessage = (meetValue, checkSelectedBidStrategy) => {
    if (checkSelectedBidStrategy) {
      if (meetValue === -1) {
        this.checkConversionCountAndRevenue();
        return false;
      }
      if (!this.isBulkEdit && this.selectedBidStrategy() === this.originalBidStrategy) {
        return false;
      }
      if (meetValue === 1) {
        return false;
      } else if (meetValue === 0) {
        return true;
      } else { //should not be come here.
        return true;
      }
    } else {
      return false;
    }
  };

  this.showConversionCountCheckErrorMessage = ko.computed(() => {
    return this.checkErrorMessage(
      this.meetConversionGoal(),
      this.actualBidStrategy() === types.maximizeConversions || this.actualBidStrategy() === types.targetCpa || this.actualBidStrategy() === types.targetRoas,
    );
  });

  this.showRevenueCheckErrorMessage = ko.computed(() => {
    if (isCampaignTargetROASBidStrategyEnabled) {
      return this.checkErrorMessage(
        this.meetRevenueGoal(),
        this.actualBidStrategy() === types.targetRoas || this.actualBidStrategy() === types.maxConversionValue,
      );
    } else {
      return false;
    }
  });

  this.meetMaxConversionValueGoal = ko.observable(-1); // initialize with -1 to indicated not get odata result yet. 0 means not met and 1 means campaign met the max conversion value goals


  this.showMaxConversionValueLocationTargetMessage = ko.computed(() => {
    if (this.campaignSubType === 'ShoppingSmartAds') {
      return false;
    }
    if (this.isPmax) {
      return false;
    }
    if (this.isSearchCampaignMaxConversionValueEnabled) {
      return false;
    }
    if (isMaximizeConversionValueEnabled && this.selectedBidStrategy() === types.maxConversionValue) {
      return true;
    } else {
      return false;
    }
  });

  this.showManualCpcDeprecationBannerForAudienceCampaign = ko.computed(() => {
    if (this.selectedBidStrategy() === types.manual && this.campaignType === 'audience' && !this.isVideo) {
      return true;
    }

    return false;
  });

  // We won't display warning in adgroup or bulk edit case or non-audience campaign
  this.showSwitchStrategyWarningMessage = ko.computed(() =>
    model.entityType === 'Campaign' &&
    this.campaignType === 'audience' && !isXandrServing(this.campaignSubType, this.campaignSettings) &&
    this.selectedBidStrategy().toLowerCase() !== this.originalBidStrategy.toLowerCase() && !this.isBulkEdit && !this.isCreation,
  );

  // pilot on & search campaign & broad only setting on & not supported strategy options -> block
  this.showBroadOnlyErrorMessage = ko.observable(false);
  this.isBroadMatchOnlyCampaign = ko.observable(model.isBroadMatchOnlyCampaign);
  this.isBroadMatchOnlyCampaign.subscribe((value) => {
    if (isBroadOnlyPilotEnabled && !isPredictiveMatchingPilotEnabled && model.entityType === 'Campaign' && this.campaignType === 'search') {
      this.showBroadOnlyErrorMessage(value && !BROAD_ONLY_BID_STRATEGY_OPTIONS.has(this.selectedBidStrategy()));
    }
  });
  this.selectedBidStrategy.subscribe((value) => {
    if (isBroadOnlyPilotEnabled && !isPredictiveMatchingPilotEnabled && model.entityType === 'Campaign' && this.campaignType === 'search') {
      this.showBroadOnlyErrorMessage(this.isBroadMatchOnlyCampaign() && !BROAD_ONLY_BID_STRATEGY_OPTIONS.has(value));
    }
  });

  if (this.isInitialize === true) {
    this.checkConversionTracking();
  }

  this.errorMessages = () => {
    const maxCpc = [this.errorMessageMaxCpc()];
    const targetCpa = [this.errorMessageTargetCpa()];
    const targetRoas = [this.errorMessageTargetRoas()];
    const maxConversionValue = [this.errorMessageMaxConversionValue()];
    const targetImpressionSharePercent = [this.errorMessageTargetImpressionSharePercent()];
    const costPerSale = [this.errorMessageCostPerSale()];
    const targetCommissionRate = [this.errorMessageTargetCommissionRate()];

    return {
      maxCpc,
      targetCpa,
      targetRoas,
      maxConversionValue,
      targetImpressionSharePercent,
      costPerSale,
      targetCommissionRate,
    };
  };

  this.validate = () => {
    this.errorMessageMaxCpc('');
    this.errorMessageTargetCpa('');
    this.errorMessageTargetRoas('');
    this.errorMessageMaxConversionValue('');
    this.errorMessageTargetImpressionSharePercent('');
    this.errorMessagePortfolioBidStrategy('');
    this.errorMessageCostPerSale('');
    this.errorMessageTargetCommissionRate('');

    if (this.showConversionCountCheckErrorMessage()) {
      return false;
    }

    if (this.showRevenueCheckErrorMessage()) {
      return false;
    }

    if (this.showBroadOnlyErrorMessage()) {
      return false;
    }

    if (this.selectedBidStrategy() === types.maximizeClicks ||
      this.selectedBidStrategy() === types.maximizeConversions ||
      this.selectedBidStrategy() === types.targetCpa ||
      (this.selectedBidStrategy() === types.targetRoas || (this.isTargetRoasCheckboxSelected() && this.selectedBidStrategy() !== types.maxConversionValue)) ||
      this.selectedBidStrategy() === types.targetImpressionShare) {
      if (this.checkCPC()) {
        const maxCpc = this.maxCpc();
        const maxCpcKey = `maxCpc${isBidLessThanFiveCentsForShoppingCampaign(this.campaignType, this.isBulkEdit) ? 'ProductGroup' : 'Keyword'}`;
        const result = validateNumber(maxCpc, maxCpcKey, false, false, this.isMaxCpcDirty());

        if (!_.isEmpty(result)) {
          this.errorMessageMaxCpc(result);
          return false;
        }
      }
    }

    if (this.selectedBidStrategy() === types.targetCpa ||
      (this.selectedBidStrategy() === types.maximizeConversions && this.isTargetCpaCheckboxSelected() && (this.isAudienceCampaignMaxConversionsEnabled || isMaxConversionForSscEnabled || isChinaMarketAudienceCampaign))
    ) {
      const targetCpa = this.targetCpa();
      const result = validateNumber(targetCpa, 'targetCpa', false, false, this.selectedBidStrategy() === types.targetCpa ? true : this.isTargetCpaDirty());

      if (!_.isEmpty(result)) {
        this.errorMessageTargetCpa(result);
        return false;
      }
    }

    if (isCampaignTargetROASBidStrategyEnabled && this.selectedBidStrategy() === types.targetRoas) {
      const targetRoas = this.targetRoas();
      const result = validateNumber(targetRoas, 'targetRoas');

      if (!_.isEmpty(result)) {
        this.errorMessageTargetRoas(result);
        return false;
      }
    }

    if (this.selectedBidStrategy() === types.maxConversionValue && this.isTargetRoasCheckboxSelected()) {
      if (!this.isTargetRoasCheckboxSelected()) {
        return true;
      }
      const targetRoas = this.targetRoas();
      const result = validateNumber(targetRoas, 'targetRoas');

      if (!_.isEmpty(result)) {
        this.errorMessageTargetRoas(result);
        return false;
      }
    }

    if (isTargetImpressionShareEnabled && this.selectedBidStrategy() === types.targetImpressionShare) {
      const targetImpressionShare = this.targetImpressionShare();
      const result = validateNumber(targetImpressionShare, 'targetImpressionShare');

      if (!_.isEmpty(result)) {
        this.errorMessageTargetImpressionSharePercent(result);
        return false;
      }
    }

    if (isPortfolioBidStrategyEnabled && this.selectedBidStrategy() === types.portfolioBidStrategy) {
      const portfolioBidStrategy = this.selectedPortfolioBidStrategy();

      if (!portfolioBidStrategy) {
        this.errorMessagePortfolioBidStrategy(this.i18nNewDesign.getString(_TL_('You haven\'t selected any portfolio bid strategy.')));
        return false;
      }
    }

    if (isCostPerSaleEnabled && this.selectedBidStrategy() === types.costPerSale) {
      const costPerSale = this.costPerSale();
      let result = validateNumber(costPerSale, 'costPerSale');

      if (this.validateWithBudget) {
        const vCps = decimalNumber.fromLocalToFloat(costPerSale);
        const vBudget = decimalNumber.fromLocalToFloat(this.budgetAmount);

        if (_.isEmpty(result) && vCps > vBudget) {
          result = i18n.getString('BidStrategySetting_Message_Bid_GreaterThanBudget');
        }
      }

      if (!_.isEmpty(result)) {
        this.errorMessageCostPerSale(result);
        return false;
      }
    }

    if (this.selectedBidStrategy() === types.commission) {
      const targetCommissionRate = this.targetCommissionRate();
      const result = validateNumber(targetCommissionRate, 'targetCommissionRate', true);

      if (!_.isEmpty(result)) {
        this.errorMessageTargetCommissionRate(result);
        return false;
      }
    }

    return true;
  };

  this.getValues = () => {
    if (isPortfolioBidStrategyEnabled && this.selectedBidStrategy() === types.portfolioBidStrategy) {
      const portfolioBidStrategy = this.selectedPortfolioBidStrategy();

      if (portfolioBidStrategy && portfolioBidStrategy.Id) {
        return {
          BidStrategyId: portfolioBidStrategy.Id,
          BidStrategyName: portfolioBidStrategy.Name,
          Type: portfolioBidStrategy.Type,
        };
      } else {
        // does not change selected portfolio bid strategy,
        // in that case we don't know what's the selected portfolio bid strategy details (like id)
        return {};
      }
    }

    let value = {
      Type: this.selectedBidStrategy(),
    };

    if (this.selectedBidStrategy() === types.maximizeClicks ||
      this.selectedBidStrategy() === types.maximizeConversions ||
      this.selectedBidStrategy() === types.targetCpa ||
      (this.selectedBidStrategy() === types.targetRoas || this.isTargetRoasCheckboxSelected()) ||
      this.selectedBidStrategy() === types.targetImpressionShare) {
      if (this.checkCPC()) {
        value.MaxCpc = {
          Amount: decimalNumber.fromLocalToFloat(this.maxCpc()),
        };
      }
    }

    if (this.selectedBidStrategy() === types.targetCpa || (this.selectedBidStrategy() === types.maximizeConversions && this.isTargetCpaCheckboxSelected())) {
      value.TargetCpa = decimalNumber.fromLocalToFloat(this.targetCpa());
    }
    if (isRSABidStrategyAutoTargetingEnabled && this.showAutoTargeting() && this.checkAutoTargeting()) {
      value.AutoTargetingEnabled = true;
    }
    if ((isCampaignTargetROASBidStrategyEnabled && this.selectedBidStrategy() === types.targetRoas) || (this.selectedBidStrategy() === types.maxConversionValue && this.isTargetRoasCheckboxSelected())) {
      value.TargetRoas = decimalNumber.fromLocalToFloat(this.targetRoas()) / this.multiplier;
    }
    if (isTargetImpressionShareEnabled && this.selectedBidStrategy() === types.targetImpressionShare) {
      value.TargetAdPosition = this.targetImpressionShareAdPosition();
      value.TargetImpressionShare = decimalNumber.fromLocalToFloat(this.targetImpressionShare());
    }
    if (this.originalBidStrategy === types.portfolioBidStrategy) {
      // If the campaign was associated with portfolio bid strategy, disassociate with it
      value.BidStrategyId = 0;
    }

    if (this.selectedBidStrategy() === types.costPerSale) {
      value.CostPerSale = decimalNumber.fromLocalToFloat(this.costPerSale());
    }

    if (this.selectedBidStrategy() === types.maxConversionValue &&
      this.campaignSubType !== 'ShoppingSmartAds' && !this.isPmax && !this.isSearchCampaignMaxConversionValueEnabled && !isSearchShoppingWithMaxConversionValue) {
      value.TargetRoas = null;
    }

    return value;
  };

  this.showBidStrategySection = ko.computed(() => {
    if (this.forceHideBidStrategySection()) {
      return false;
    }

    // Hide the bid strategy section in ad group creation and ad group settings for supported campaign types (see shouldEnableManualBiddingDeprecation()). We don't want to hide the section during a bulk edit of ad groups.
    // Bulk edit in the ad groups grid for both legacy and new UI also uses this viewmodel for the bid strategy dropdown. We want to show the section (return true) in that case and any other cases.
    if (model.entityType === 'AdGroup' && !this.isBulkEdit) {
      if (model.isMSStoreAdsGAWorkflow) {
        return false;
      }
      // We should hide bid strategy section in adgroup belonging to Audience campaign
      if (this.campaignType === 'audience') {
        return false;
      }
      if (this.campaignBiddingStrategyType === types.costPerSale) {
        return false;
      }
      return !this.shouldEnableManualBiddingDeprecation();
    } else {
      return true;
    }
  });

  this.update = function (mtModel) {
    if (mtModel.TargetCpa) {
      this.isTargetCpaCheckboxSelected(true);
      this.targetCpa(decimalNumber.fromFloatToString(mtModel.TargetCpa, 2));
    }
    if (mtModel.TargetRoas) {
      this.isTargetRoasCheckboxSelected(true);
      this.targetRoas(decimalNumber.fromFloatToString(mtModel.TargetRoas * this.multiplier, 2));
    }
  };
}

export const testables = {
  NumberRange,
};
